import React, { useState } from "react";
import Octicon from "react-octicon";
import { DropzoneButton } from "AppSrc/utils/dropZone/dropZoneHooks";
import { Tooltip } from "react-tippy";
import { BSBtn, BSBtnDropdown } from "traec-react/utils/bootstrap";
import DocumentHistoryRows from "./documentHistoryRows";
import { ReportDocumentFormButton } from "./reportDocumentForm";
import { ReportDocumentVerticalFormButton } from "./reportDocumentFormVertical";
import { ErrorBoundary } from "storybook-dashboard/errors/handleError";
import { confirmDelete } from "traec-react/utils/sweetalert";
import { RequiredStar } from "AppSrc/project/metrics/documentRow";
import useApi, { doFetch, fetcher } from "storybook-dashboard/utils/fetching";
import { useProjectContext } from "storybook-dashboard/context/project";
import { useFullIds, useProjectPermissions } from "storybook-dashboard/utils/hooks";
import { mergeNodes } from "storybook-dashboard/utils";
import { useModal, Modal } from "storybook-dashboard/components/modal";
import { alertSuccess } from "traec-react/utils/sweetalert";
import { Hider } from "storybook-dashboard/utils";

export function DocumentTitleTooltip({ doc, indentLevel, isProjectAdmin }) {
  let title = doc ? doc.getIn(["description", "title"]) || doc.get("name").substring(0, 8) : null;

  let descr = doc.getIn(["description", "text"]) || "";
  let toolTipHtml = (
    <div className="text-left">
      <div dangerouslySetInnerHTML={{ __html: descr.trim() }} />
      {isProjectAdmin ? <p style={{ marginTop: "1rem" }}>Doc id: {doc?.get("uid")?.substring(0, 8)}</p> : null}
    </div>
  );

  return (
    <div style={{ margin: "0", marginLeft: `${(indentLevel + 1) * 1.5}em` }}>
      {title}
      <RequiredStar document={doc} />
      <Tooltip animateFill={false} html={toolTipHtml}>
        <Octicon name="info" className="ml-2" />
      </Tooltip>
    </div>
  );
}

export const downloadS3Object = ({ trackerId, commitId, docId, currentDocObject }) => {
  let docObjectId = currentDocObject.get("uid");

  fetcher(`/api/tracker/${trackerId}/commit/${commitId}/document/${docId}/object/${docObjectId}/?url=true`, "GET")
    .then(({ payload }) => {
      let signed_url = payload.get("signed_url");
      if (signed_url) {
        console.log("Downloading file", signed_url);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = signed_url;
        a.download = payload.get("filename") || "unknown.file";
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
    })
    .catch((err) => {
      failureHandler ? failureHandler(err) : null;
    });
};

export function CurrentObject(props) {
  let { currentDocObject, errors, status, hide, noFileMessage } = props;
  if (hide) {
    return null;
  }

  if (!currentDocObject?.get("uid")) {
    return <span className="float-left text-muted">{noFileMessage ? noFileMessage : `No file`}</span>;
  }

  if (status && status === "sent") {
    return <span className="alert-warning float-left">Uploading... please wait.</span>;
  }

  if (errors || status === "failed") {
    console.log("ERRORS IN DOCUMENT UPLOAD", errors);
    if (errors === "Request Entity Too Large") {
      errors = "Document too large! Maximum allowed upload size is 500Mb";
    } else {
      errors = "Error in upload.  Maximum allowed upload size is 500Mb";
    }
    return <span className="alert-danger float-left">{errors}</span>;
  }

  let filename = currentDocObject ? currentDocObject.get("filename") : null;
  let url = currentDocObject ? currentDocObject.get("signed_url") : null;

  let isUploaded = currentDocObject ? !(currentDocObject.created && currentDocObject.virus_checked) : false;

  return (
    <ErrorBoundary>
      {isUploaded ? <Octicon name="check" className="float-left" /> : <Octicon name="file" />}

      <a
        style={{ cursor: "pointer", color: "#007bff", textDecoration: "underline" }}
        className="link-primary"
        onClick={(e) => {
          e.preventDefault();
          downloadS3Object({ ...props });
        }}
      >
        {filename}
      </a>
    </ErrorBoundary>
  );
}

export const getPtr = ({ commitId, refId }) => ({
  ptrId: commitId || refId,
  ptr: commitId ? "commit" : "ref",
});

const updateDocStatus = ({ trackerId, commitId, refId, path, statusName, triggerObjectList, fetches }) => {
  let { ptr, ptrId } = getPtr({ commitId, refId });
  doFetch(`/api/tracker/${trackerId}/${ptr}/${ptrId}/node/${path}/`, "PUT", {
    type: "document",
    node: {
      document: {
        status: {
          status: { name: statusName },
        },
      },
    },
  })
    .then((r) => r.json())
    .then((newData) => {
      let { data, mutate } = fetches?.nodes;
      mutate(mergeNodes(data, newData));
    });
};

export function DocumentNoReportCheckbox(props) {
  let { path: pathId, docStatus, setNoReport, readOnly } = props;
  return (
    <input
      type="checkbox"
      disabled={readOnly}
      className={`m-0 p-0`}
      defaultChecked={docStatus?.getIn(["status", "name"]) == "Not for Submission"}
      onChange={(e) => {
        let statusName = e.target.checked ? "Not for Submission" : "OK for Submission";
        console.log("CHECKED NO REPORT for document at path", pathId, docStatus?.toJS());
        console.log("Changing document status to: ", statusName);
        updateDocStatus({ ...props, statusName });
        setNoReport(e.target.checked);
      }}
      name={"noreport"}
    />
  );
}

function SelectFile(props) {
  let {
    hide,
    doc,
    disableInputs,
    selectedFiles,
    onDrop,
    onCancelUpload,
    onConfirmHandler,
    isUploadingFile,
    startIsUploadingFile,
    stopIsUploadingFile,
  } = props;

  if (hide || disableInputs) {
    return null;
  }

  let files = selectedFiles.map((file) => (
    <a key={file.name}>
      Upload {file.name}? ({(file.size / 1e6).toFixed(1)}Mb)
    </a>
  ));

  // Give a warning if the file is too large
  let confirmButton = null;
  if (selectedFiles.filter((file) => file.size / 1e6 > 500).length) {
    files = [
      <span key={0} className="alert-danger">
        Maximum allowed upload size is 500Mb
      </span>,
    ];
  } else {
    confirmButton = (
      <BSBtn text={"Upload"} onClick={onConfirmHandler} extra_className="pl-1 pr-1 m-0 p-0" noFloatRight={true} />
    );
  }

  return (
    <div className="float-right">
      <DropzoneButton
        onDrop={onDrop}
        extra_className="pl-1 pr-1 m-0 p-0"
        selectAreaText="Click or drop here to add files"
        confirmButton={confirmButton}
        selectedFiles={files}
        onCancelUpload={onCancelUpload}
        isUploadingFile={isUploadingFile}
        startIsUploadingFile={startIsUploadingFile}
        stopIsUploadingFile={stopIsUploadingFile}
      />
    </div>
  );
}

const uploadFile = (props) => {
  let { trackerId, commitId, path, file, triggerObjectList, fetches } = props;

  // Add the file object to the form and dispatch
  let formData = new FormData();
  formData.append("fileobj", file);
  formData.append("type", "document");
  formData.append("path", path);

  return doFetch(`/api/tracker/${trackerId}/commit/${commitId}/node/${path}/`, "PUT", formData)
    .then((r) => r.json())
    .then((newData) => {
      let { data, mutate } = fetches.nodes;
      mutate(mergeNodes(data, newData));
      triggerObjectList();
    });
};

const clearFile = (props) => {
  let { trackerId, commitId, refId, docStatus, fetches } = props;
  confirmDelete({
    text: `This will clear the current uploaded document.  It will still appear in the upload history.  Would you like to proceed?`,
    onConfirm: () => {
      let path = docStatus.get("_path");
      let { ptr, ptrId } = getPtr({ commitId, refId });
      doFetch(`/api/tracker/${trackerId}/${ptr}/${ptrId}/node/${path}/`, "PATCH", {
        type: "documentstatus",
        node: {
          documentstatus: {
            current_object: null,
          },
        },
      })
        .then((r) => r.json())
        .then((newData) => {
          let { data, mutate } = fetches.nodes;
          mutate(mergeNodes(data, newData));
        });
    },
  });
};

const getByType = (nodes, _type) => {
  return nodes.filter((i) => i.get("_type") == _type)?.first();
};

function FileUploadModalButton(props) {
  let { path } = props;
  let { setModal } = useModal();
  let { trackerId, commitId } = useFullIds();
  let { getNode } = useProjectContext();
  let doc = getNode(path);
  let docId = doc?.get("uid");
  let { data: docObjects, trigger: triggerObjectList } = useApi(
    "/api/tracker/{trackerId}/commit/{commitId}/document/{docId}/object/?thisCommitOnly=true",
    { trackerId, commitId, docId }
  );

  return (
    <button
      className={`btn btn-sm m-0 p-0 pl-1 pr-1 ${docObjects?.size ? "btn-primary" : "btn-secondary"}`}
      onClick={() =>
        setModal(
          <Modal title={`${doc.get("name")} uploads`}>
            <table className="table table-sm table-striped">
              <tbody>
                <DocumentHistoryRows {...props} hide={false} indentLevel={0} />
              </tbody>
            </table>
          </Modal>
        )
      }
    >
      {docObjects?.size} files
    </button>
  );
}

export default function ReportDocumentRow(props) {
  let { trackerId, refId, commitId } = useFullIds();
  let { getNode, getChildren, fetches, commit } = useProjectContext();
  let { permissions } = useProjectPermissions();
  let { rowColor, indentLevel, path, disableInputs } = props;

  let [selectedFiles, setSelectedFiles] = useState([]);
  let [isUploadingFile, setIsUploadingFile] = useState(false);

  let isProjectAdmin = permissions?.get("isAdmin");

  let doc = getNode(path);
  let description = getByType(getChildren(doc), "trackerdescription");
  let docStatus = getByType(getChildren(doc), "documentstatus");
  let currentDocObject = docStatus?.get(`current_object`, null);

  // useFullIds relies on useParams which breaks when being used in a global modal (SubmitModal) so get trackerId/refId/commitId from props
  if (!trackerId || !commitId || !refId) {
    trackerId = props.context.project?.getIn(["trackers", 0, "uid"]);
    commitId = props.context.commit?.get("uid");
    refId = props.context.commit?.get("ref");
  }

  doc = doc.set("description", description);

  let docId = doc?.get("uid");
  let { data: docObjects, trigger: triggerObjectList } = useApi(
    "/api/tracker/{trackerId}/commit/{commitId}/document/{docId}/object/?thisCommitOnly=true",
    { trackerId, commitId, docId }
  );

  let [noReport, setNoReport] = useState(docStatus?.getIn(["status", "name"]) == "Not for Submission");
  let isForm = doc?.getIn(["meta_json", "input_type"]) == "form";
  let isFormVertical = doc?.getIn(["meta_json", "input_type"]) == "formvertical";
  let hideNA = doc?.getIn(["meta_json", "hideNA"]) == true;
  console.log(hideNA, "hideNA in ReportDocumentRow", doc?.getIn(["meta_json", "hideNA"]));

  let _props = {
    ...props,
    commit,
    doc,
    docStatus,
    currentDocObject,
    selectedFiles,
    setSelectedFiles,
    setNoReport,
    trackerId,
    commitId,
    docId,
    triggerObjectList,
    fetches,
    readOnly: disableInputs,
  };

  if (isFormVertical) {
    return <ReportDocumentVerticalFormButton {..._props} />;
  }

  if (isForm) {
    return <ReportDocumentFormButton {..._props} />;
  }

  const onDrop = (files) => {
    // Confirm any of the files are not greater than 500Mb
    let bigFiles = files.filter((_file) => _file.size / 1e6 > 500);
    console.log("SelectFile checking big files", bigFiles);
    if (bigFiles?.length) {
      alertSuccess({
        iconType: "warning",
        title: "Files are too large",
        text: `Some files exceed the upload limit of 500MB`,
      });
      return;
    }

    setSelectedFiles(files);
    setIsUploadingFile(true);

    // Wait for all files to upload and then reset state
    let uploads = files.map((_file) => uploadFile({ ..._props, file: _file }));
    Promise.all(uploads).then(() => {
      setSelectedFiles([]);
      setIsUploadingFile(false);
    });
  };

  const onCancelUpload = () => {
    setSelectedFiles([]);
    setIsUploadingFile(false);
  };

  return (
    <>
      <tr style={{ backgroundColor: rowColor }}>
        <td className="border-0">
          <DocumentTitleTooltip doc={doc} indentLevel={indentLevel} isProjectAdmin={isProjectAdmin} />
        </td>

        <td colSpan={disableInputs ? 2 : 3} className="border-0">
          <FileUploadModalButton {..._props} />
          <SelectFile
            {...props}
            hide={noReport}
            selectedFiles={selectedFiles}
            onDrop={onDrop}
            onCancelUpload={onCancelUpload}
            isUploadingFile={isUploadingFile}
          />
        </td>
        <td className="text-center border-0">
          <Hider hide={hideNA}>
            <DocumentNoReportCheckbox
              {..._props}
              onChange={(e) => {
                setSelectedFiles([]);
                setIsUploadingFile(false);
                setNoReport(true);
              }}
            />
          </Hider>
        </td>
        <td className="border-0"></td>
      </tr>
    </>
  );
}
